import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Button } from "../../components/ui";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Filler,
    Tooltip,
    Legend,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    IoCashOutline,
    IoClipboardOutline,
    IoBusinessOutline,
    IoPeopleOutline,
    IoBodyOutline,
    IoLocationOutline,
    IoBookmarksOutline,
} from "react-icons/io5";
import GetData from "../../services/getData";
import { getLocale } from "../../i18n/config";
import { useUserSettings } from "../../context/UserSettingsContext";
import TitleWithIcon from "../../components/layout/pageTitle/PageTitle";
import BlockTitle from "../../components/layout/blockTitle/BlockTitle";
import scheduleIcon from "../../media/icons8-appointments.png";
import "./dashboard.css";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Filler,
    Tooltip,
    Legend
);

const DAY_START_HOUR = 8;
const DAY_END_HOUR = 20;
const DEFAULT_COMPANY_ID = "1216181";

function toLocalDateStr(d = new Date()) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

function parseLocalDate(dateStr) {
    const [y, m, d] = String(dateStr).split("-").map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
}

function getStaffName(staff) {
    return staff?.name || staff?.nickname || "—";
}

function getStaffList(rec) {
    const list = [];
    if (Array.isArray(rec.staff_list) && rec.staff_list.length > 0) {
        rec.staff_list.forEach((s) => {
            const id = s?.id ?? s;
            if (id != null) list.push({ id, name: getStaffName(s), raw: s });
        });
    }
    if (list.length > 0) return list;
    const single = rec.staff;
    if (single && (single.id != null || single.id === 0)) {
        return [{ id: single.id, name: getStaffName(single), raw: single }];
    }
    const sid = rec.staff_id;
    if (sid != null) {
        return [{ id: sid, name: getStaffName(rec.staff) || "—", raw: rec.staff }];
    }
    return [];
}

function getAttendance(rec) {
    const value = rec.attendance ?? rec.visit_attendance;
    return Number(value);
}

function isDeletedRecord(rec) {
    return rec?.deleted === 1 || rec?.deleted === true;
}

function getRecordStart(rec) {
    const raw = rec.datetime || rec.date;
    if (!raw) return null;
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
}

function getRecordDurationMs(rec) {
    const durationSec = rec.seance_length ?? rec.length ?? 1800;
    return Math.max(15 * 60, Number(durationSec) || 1800) * 1000;
}

function getMonthCells(year, month) {
    const first = new Date(year, month, 1);
    let startWeekday = first.getDay();
    startWeekday = startWeekday === 0 ? 6 : startWeekday - 1;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startWeekday; i += 1) cells.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
}

function formatWeekday(dateStr, locale) {
    const d = parseLocalDate(dateStr);
    const weekday = d.toLocaleDateString(locale, { weekday: "long" });
    return weekday.charAt(0).toUpperCase() + weekday.slice(1);
}

function formatDateLong(dateStr, locale) {
    const d = parseLocalDate(dateStr);
    return d.toLocaleDateString(locale, { day: "numeric", month: "long" });
}

/** Короткие названия дней недели (Пн..Вс -> по локали), неделя с понедельника. */
function getWeekdayLabels(locale) {
    // 1 января 2024 — понедельник.
    return Array.from({ length: 7 }, (_, i) =>
        new Intl.DateTimeFormat(locale, { weekday: "short" }).format(
            new Date(2024, 0, 1 + i)
        )
    );
}

function shiftDateStr(dateStr, days) {
    const d = parseLocalDate(dateStr);
    d.setDate(d.getDate() + days);
    return toLocalDateStr(d);
}

function toNumber(value) {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
}

function getRecordCost(rec) {
    const services = Array.isArray(rec.services) ? rec.services : [];
    const servicesCost = services.reduce(
        (sum, s) => sum + toNumber(s.cost ?? s.manual_cost ?? s.first_cost),
        0
    );
    const goods = Array.isArray(rec.goods_transactions) ? rec.goods_transactions : [];
    const goodsCost = goods.reduce(
        (sum, g) => sum + toNumber(g.cost ?? g.amount ?? g.price),
        0
    );
    if (servicesCost + goodsCost > 0) return servicesCost + goodsCost;
    return toNumber(rec.cost);
}

function summarizeRecords(list) {
    let came = 0;
    let noShow = 0;
    let waiting = 0;
    let confirmed = 0;
    let refused = 0;
    let totalCost = 0;
    list.forEach((rec) => {
        const attendance = getAttendance(rec);
        if (attendance === 1) came += 1;
        else if (attendance === -1) noShow += 1;
        else if (attendance === 2) confirmed += 1;
        else if (attendance === 3) refused += 1;
        else waiting += 1;
        totalCost += getRecordCost(rec);
    });
    return {
        total: list.length,
        came,
        noShow,
        waiting,
        confirmed,
        refused,
        totalCost,
    };
}

function getTopServices(list, limit = 7) {
    const map = new Map();
    list.forEach((rec) => {
        const services = Array.isArray(rec.services) ? rec.services : [];
        services.forEach((s) => {
            const name = s?.title || s?.name;
            if (!name) return;
            if (!map.has(name)) {
                map.set(name, { name, count: 0 });
            }
            map.get(name).count += 1;
        });
    });
    return Array.from(map.values())
        .sort((a, b) => b.count - a.count)
        .slice(0, limit);
}

function formatMoney(n) {
    return new Intl.NumberFormat("ru-RU", {
        style: "currency",
        currency: "RUB",
        maximumFractionDigits: 0,
    }).format(n || 0);
}

function countApiList(res) {
    const total = res?.pagination?.total;
    if (total != null && total !== "") return Number(total) || 0;
    const list = res?.result ?? res?.request?.result ?? res?.data;
    return Array.isArray(list) ? list.length : 0;
}

function CatalogCard({ to, icon, label, value }) {
    return (
        <Link to={to} className="page-block dashboard-kpi dashboard-kpi-stat dashboard-catalog-card">
            <span className="dashboard-kpi-icon" aria-hidden="true">
                {icon}
            </span>
            <div className="dashboard-kpi-fields">
                <div className="dashboard-stat-label">{label}</div>
                <div className="dashboard-kpi-stat-value-row">
                    <div className="dashboard-stat-value">{value}</div>
                </div>
            </div>
        </Link>
    );
}

function useNow(intervalMs = 1000) {
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), intervalMs);
        return () => clearInterval(id);
    }, [intervalMs]);
    return now;
}

function DashboardCalendar({ date, onChange }) {
    const { t, i18n } = useTranslation("dashboard");
    const locale = getLocale(i18n.language);
    const selected = parseLocalDate(date);
    const todayStr = toLocalDateStr();
    const [viewYear, setViewYear] = useState(selected.getFullYear());
    const [viewMonth, setViewMonth] = useState(selected.getMonth());

    const weekdayLabels = useMemo(() => getWeekdayLabels(locale), [locale]);
    const monthLabel = useMemo(
        () =>
            new Intl.DateTimeFormat(locale, { month: "long" }).format(
                new Date(viewYear, viewMonth, 1)
            ),
        [locale, viewYear, viewMonth]
    );

    useEffect(() => {
        const d = parseLocalDate(date);
        setViewYear(d.getFullYear());
        setViewMonth(d.getMonth());
    }, [date]);

    const cells = useMemo(
        () => getMonthCells(viewYear, viewMonth),
        [viewYear, viewMonth]
    );

    const shiftMonth = (delta) => {
        const next = new Date(viewYear, viewMonth + delta, 1);
        setViewYear(next.getFullYear());
        setViewMonth(next.getMonth());
    };

    return (
        <div className="dashboard-calendar">
            <div className="dashboard-calendar-header">
                <button
                    type="button"
                    className="dashboard-calendar-nav"
                    onClick={() => shiftMonth(-1)}
                    aria-label={t("calendar.prevMonth")}
                >
                    ‹
                </button>
                <div className="dashboard-calendar-title">
                    {monthLabel} {viewYear}
                </div>
                <button
                    type="button"
                    className="dashboard-calendar-nav"
                    onClick={() => shiftMonth(1)}
                    aria-label={t("calendar.nextMonth")}
                >
                    ›
                </button>
            </div>
            <div className="dashboard-calendar-weekdays">
                {weekdayLabels.map((day) => (
                    <div key={day} className="dashboard-calendar-weekday">
                        {day}
                    </div>
                ))}
            </div>
            <div className="dashboard-calendar-grid">
                {cells.map((day, idx) => {
                    if (!day) {
                        return <div key={`empty-${idx}`} className="dashboard-calendar-cell is-empty" />;
                    }
                    const cellDate = toLocalDateStr(new Date(viewYear, viewMonth, day));
                    const isSelected = cellDate === date;
                    const isToday = cellDate === todayStr;
                    return (
                        <button
                            key={cellDate}
                            type="button"
                            className={[
                                "dashboard-calendar-cell",
                                isSelected ? "is-selected" : "",
                                isToday ? "is-today" : "",
                            ]
                                .filter(Boolean)
                                .join(" ")}
                            onClick={() => onChange(cellDate)}
                        >
                            {day}
                        </button>
                    );
                })}
            </div>
            {date !== todayStr && (
                <Button
                    className="dashboard-calendar-today"
                    onClick={() => onChange(todayStr)}
                    size="sm"
                    variant="secondary">
                    {t("calendar.today")}
                </Button>
            )}
        </div>
    );
}

function Dashboard() {
    const { t, i18n } = useTranslation("dashboard");
    const locale = getLocale(i18n.language);
    const { get, update } = useUserSettings();
    const [date, setDate] = useState(() => toLocalDateStr());
    const [branch, setBranch] = useState(() =>
        String(get("filters.dashboard.clinic_id", "") || "")
    );
    const [clinics, setClinics] = useState([]);
    const [records, setRecords] = useState([]);
    const [prevRecords, setPrevRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [catalogCounts, setCatalogCounts] = useState({
        staff: null,
        services: null,
        clinics: null,
        templates: null,
    });

    const clinicsWithYc = useMemo(
        () =>
            clinics.filter(
                (c) =>
                    (c.id_yc != null && c.id_yc !== "") ||
                    (c.id_al != null && c.id_al !== "")
            ),
        [clinics]
    );

    const loadClinics = useCallback(async () => {
        try {
            const res = await GetData({
                module: "schedule",
                block: "list",
                action: "get_clinics",
            });
            if (res && res.success && Array.isArray(res.data)) {
                setClinics(res.data);
                const withYc = res.data.filter(
                    (c) =>
                        (c.id_yc != null && c.id_yc !== "") ||
                        (c.id_al != null && c.id_al !== "")
                );
                // Сохранённый пользователем филиал приоритетнее авто-выбора.
                const savedBranch = String(get("filters.dashboard.clinic_id", "") || "");
                if (savedBranch) {
                    setBranch(savedBranch);
                } else {
                    const preferred =
                        withYc.find((c) => String(c.id_yc) === DEFAULT_COMPANY_ID) || withYc[0];
                    if (preferred) {
                        setBranch(String(preferred.id));
                    } else {
                        setLoading(false);
                        setError(t("errors.noBranchBooking"));
                    }
                }
            } else {
                setLoading(false);
                setError(t("errors.clinicsLoadFailed"));
            }
        } catch (err) {
            console.error("Ошибка загрузки списка филиалов:", err);
            setLoading(false);
            setError(t("errors.clinicsLoadFailed"));
        }
    }, [t, get]);

    useEffect(() => {
        loadClinics();
    }, [loadClinics]);

    const loadSummary = useCallback(async () => {
        if (!branch) return;
        setError(null);
        setLoading(true);
        const prevDate = shiftDateStr(date, -1);
        try {
            const [res, prevRes] = await Promise.all([
                GetData({
                    module: "schedule",
                    block: "list",
                    action: "get",
                    date: date || undefined,
                    clinic_id: branch || undefined,
                }),
                GetData({
                    module: "schedule",
                    block: "list",
                    action: "get",
                    date: prevDate,
                    clinic_id: branch || undefined,
                }),
            ]);
            if (res && res.success) {
                const list = Array.isArray(res.data) ? res.data : [];
                setRecords(list.filter((rec) => !isDeletedRecord(rec)));
            } else {
                setRecords([]);
                setError(res?.message || t("errors.branchDataFailed"));
            }
            if (prevRes && prevRes.success) {
                const list = Array.isArray(prevRes.data) ? prevRes.data : [];
                setPrevRecords(list.filter((rec) => !isDeletedRecord(rec)));
            } else {
                setPrevRecords([]);
            }
        } catch (err) {
            console.error("Ошибка загрузки сводки:", err);
            setRecords([]);
            setPrevRecords([]);
            setError(t("errors.branchDataFailed"));
        } finally {
            setLoading(false);
        }
    }, [date, branch, t]);

    useEffect(() => {
        document.title = t("title");
        const handler = setTimeout(() => {
            loadSummary();
        }, 200);
        return () => clearTimeout(handler);
    }, [loadSummary, t]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [staffRes, servicesRes, clinicsRes, plansRes, recsRes] = await Promise.all([
                    GetData({ module: "staff", block: "list" }),
                    GetData({ module: "products", block: "list", page: 1, limit: 1 }),
                    GetData({ module: "contacts", block: "list" }),
                    GetData({ module: "templates", block: "treatment_plan", action: "list" }),
                    GetData({ module: "templates", block: "recommendations", action: "list" }),
                ]);
                if (cancelled) return;
                setCatalogCounts({
                    staff: countApiList(staffRes),
                    services: countApiList(servicesRes),
                    clinics: countApiList(clinicsRes),
                    templates: countApiList(plansRes) + countApiList(recsRes),
                });
            } catch (err) {
                console.error("Ошибка загрузки справочников:", err);
                if (!cancelled) {
                    setCatalogCounts({
                        staff: 0,
                        services: 0,
                        clinics: 0,
                        templates: 0,
                    });
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const stats = useMemo(() => summarizeRecords(records), [records]);
    const prevStats = useMemo(() => summarizeRecords(prevRecords), [prevRecords]);

    const now = useNow(1000);
    const timeLabel = now.toLocaleTimeString("ru-RU", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    });

    const hourlyLoad = useMemo(() => {
        const hours = [];
        for (let h = DAY_START_HOUR; h <= DAY_END_HOUR; h += 1) hours.push(h);
        const counts = hours.map((hour) => {
            let count = 0;
            records.forEach((rec) => {
                const start = getRecordStart(rec);
                if (!start) return;
                const end = new Date(start.getTime() + getRecordDurationMs(rec));
                const hourStart = parseLocalDate(date);
                hourStart.setHours(hour, 0, 0, 0);
                const hourEnd = new Date(hourStart);
                hourEnd.setHours(hour + 1, 0, 0, 0);
                if (start < hourEnd && end > hourStart) count += 1;
            });
            return count;
        });
        return {
            labels: hours.map((h) => `${String(h).padStart(2, "0")}:00`),
            counts,
        };
    }, [records, date]);

    const chartData = useMemo(
        () => ({
            labels: hourlyLoad.labels,
            datasets: [
                {
                    label: t("chart.bookings"),
                    data: hourlyLoad.counts,
                    borderColor: "#05227a",
                    backgroundColor: "rgba(5, 34, 122, 0.16)",
                    fill: true,
                    tension: 0.35,
                    pointRadius: 3,
                    pointBackgroundColor: "#05227a",
                    borderWidth: 2,
                },
            ],
        }),
        [hourlyLoad, t]
    );

    const chartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: (ctx) =>
                            t("chart.appointmentsTooltip", { count: ctx.parsed.y }),
                    },
                },
            },
            scales: {
                x: {
                    grid: { display: false },
                    ticks: { color: "#000000", maxRotation: 0, autoSkip: true },
                },
                y: {
                    beginAtZero: true,
                    ticks: {
                        color: "#000000",
                        precision: 0,
                        stepSize: 1,
                    },
                    grid: { color: "rgba(5, 34, 122, 0.08)" },
                },
            },
        }),
        [t]
    );

    const doctors = useMemo(() => {
        const map = new Map();

        records.forEach((rec) => {
            getStaffList(rec).forEach((staff) => {
                if (staff.id == null) return;
                if (!map.has(staff.id)) {
                    map.set(staff.id, {
                        id: staff.id,
                        name: staff.name,
                        count: 0,
                    });
                }
                map.get(staff.id).count += 1;
            });
        });

        return Array.from(map.values()).sort((a, b) => b.count - a.count);
    }, [records]);

    const doctorChartData = useMemo(
        () => ({
            labels: doctors.map((doc) => doc.name),
            datasets: [
                {
                    label: t("chart.appointments"),
                    data: doctors.map((doc) => doc.count),
                    backgroundColor: "#05227a",
                    borderRadius: 6,
                    barThickness: 18,
                    maxBarThickness: 22,
                },
            ],
        }),
        [doctors, t]
    );

    const doctorChartOptions = useMemo(
        () => ({
            indexAxis: "y",
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: (ctx) =>
                            t("chart.appointmentsTooltip", { count: ctx.parsed.x }),
                    },
                },
            },
            scales: {
                x: {
                    beginAtZero: true,
                    ticks: {
                        color: "#000000",
                        precision: 0,
                        stepSize: 1,
                    },
                    grid: { color: "rgba(5, 34, 122, 0.08)" },
                },
                y: {
                    grid: { display: false },
                    ticks: {
                        color: "#000000",
                        autoSkip: false,
                        callback(value) {
                            const label = this.getLabelForValue(value);
                            return label.length > 24 ? `${label.slice(0, 22)}…` : label;
                        },
                    },
                },
            },
        }),
        []
    );

    const attendanceChartData = useMemo(
        () => ({
            labels: [
                t("chart.waiting"),
                t("chart.served"),
                t("chart.noShow"),
                t("chart.confirmed"),
            ],
            datasets: [
                {
                    label: t("chart.yesterday"),
                    data: [
                        prevStats.waiting,
                        prevStats.came,
                        prevStats.noShow,
                        prevStats.confirmed,
                    ],
                    backgroundColor: "rgba(5, 34, 122, 0.22)",
                    borderRadius: 6,
                },
                {
                    label: t("chart.today"),
                    data: [stats.waiting, stats.came, stats.noShow, stats.confirmed],
                    backgroundColor: "#05227a",
                    borderRadius: 6,
                },
            ],
        }),
        [stats, prevStats, t]
    );

    const attendanceChartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    position: "top",
                    labels: { color: "#000000", boxWidth: 12, font: { size: 12 } },
                },
                tooltip: {
                    callbacks: {
                        label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y}`,
                    },
                },
            },
            scales: {
                x: {
                    grid: { display: false },
                    ticks: { color: "#000000" },
                },
                y: {
                    beginAtZero: true,
                    ticks: { color: "#000000", precision: 0, stepSize: 1 },
                    grid: { color: "rgba(5, 34, 122, 0.08)" },
                },
            },
        }),
        []
    );

    const topServices = useMemo(() => getTopServices(records, 7), [records]);

    const topServicesChartData = useMemo(
        () => ({
            labels: topServices.map((s) => s.name),
            datasets: [
                {
                    label: t("chart.bookings"),
                    data: topServices.map((s) => s.count),
                    backgroundColor: "#05227a",
                    borderRadius: 6,
                    barThickness: 16,
                    maxBarThickness: 20,
                },
            ],
        }),
        [topServices, t]
    );

    const topServicesChartOptions = useMemo(
        () => ({
            indexAxis: "y",
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: (ctx) =>
                            t("chart.bookingsTooltip", { count: ctx.parsed.x }),
                    },
                },
            },
            scales: {
                x: {
                    beginAtZero: true,
                    ticks: { color: "#000000", precision: 0, stepSize: 1 },
                    grid: { color: "rgba(5, 34, 122, 0.08)" },
                },
                y: {
                    grid: { display: false },
                    ticks: {
                        color: "#000000",
                        autoSkip: false,
                        maxWidth: 120,
                        callback(value) {
                            const label = this.getLabelForValue(value);
                            return label.length > 22 ? `${label.slice(0, 20)}…` : label;
                        },
                    },
                    afterFit(scale) {
                        scale.width = Math.min(scale.width, 128);
                    },
                },
            },
        }),
        [t]
    );

    return (
        <Container fluid>
            <Row>
                <Col>
                    <BlockTitle
                        title={
                            <TitleWithIcon
                                icon={scheduleIcon}
                                title={t("title")}
                                showStatus={false}
                                status=""
                            />
                        }
                        buttons={[]}
                    />
                </Col>
            </Row>

            <Row>
                <Col>
                    <div className="page dashboard">
                        <div className="dashboard-layout">
                            <div className="dashboard-main">
                                <div className="dashboard-kpis">
                                    <div className="page-block dashboard-kpi dashboard-kpi-branch">
                                        <div className="dashboard-kpi-fields">
                                            <div className="dashboard-kpi-label-row">
                                                <span className="dashboard-kpi-icon" aria-hidden="true">
                                                    <IoBusinessOutline />
                                                </span>
                                                <label className="dashboard-stat-label" htmlFor="dashboard-branch">
                                                    {t("branch")}
                                                </label>
                                            </div>
                                            <select
                                                id="dashboard-branch"
                                                className="dashboard-branch-select"
                                                value={branch}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setBranch(value);
                                                    // запоминаем выбор филиала в настройках пользователя
                                                    update({ filters: { dashboard: { clinic_id: value } } });
                                                }}
                                            >
                                                {clinicsWithYc.map((c) => (
                                                    <option key={c.id} value={String(c.id)}>
                                                        {c.name || t("branchFallback", { id: c.id })}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="page-block dashboard-kpi dashboard-kpi-stat">
                                        <div className="dashboard-kpi-fields">
                                            <div className="dashboard-kpi-label-row">
                                                <span className="dashboard-kpi-icon" aria-hidden="true">
                                                    <IoCashOutline />
                                                </span>
                                                <div className="dashboard-stat-label">{t("servicesAmount")}</div>
                                            </div>
                                            <div className="dashboard-kpi-stat-value-row">
                                                <div className="dashboard-stat-value">
                                                    {loading ? "…" : formatMoney(stats.totalCost)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="page-block dashboard-kpi dashboard-kpi-stat">
                                        <div className="dashboard-kpi-fields">
                                            <div className="dashboard-kpi-label-row">
                                                <span className="dashboard-kpi-icon" aria-hidden="true">
                                                    <IoClipboardOutline />
                                                </span>
                                                <div className="dashboard-stat-label">{t("records")}</div>
                                            </div>
                                            <div className="dashboard-kpi-stat-value-row">
                                                <div className="dashboard-stat-value">
                                                    {loading ? "…" : stats.total}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="page-block dashboard-chart-block">
                                    <div className="title-with-icon__title dashboard-block-title">{t("appointmentsPerDay")}</div>
                                    {error ? (
                                        <div className="dashboard-empty">{error}</div>
                                    ) : (
                                        <div className="dashboard-chart-wrap">
                                            <Line data={chartData} options={chartOptions} />
                                        </div>
                                    )}
                                </div>

                                <div className="page-block dashboard-doctors-block">
                                    <div className="title-with-icon__title dashboard-block-title">{t("loadByDoctors")}</div>
                                    {loading ? (
                                        <div className="dashboard-empty">{t("loading")}</div>
                                    ) : doctors.length === 0 ? (
                                        <div className="dashboard-empty">
                                            {t("noDoctorsOnDate")}
                                        </div>
                                    ) : (
                                        <div
                                            className="dashboard-doctors-chart"
                                            style={{
                                                minHeight: Math.max(200, doctors.length * 36),
                                            }}
                                        >
                                            <Bar data={doctorChartData} options={doctorChartOptions} />
                                        </div>
                                    )}
                                </div>

                                <div className="dashboard-main-row dashboard-main-row-3">
                                    <div className="page-block dashboard-chart-block">
                                        <div className="title-with-icon__title dashboard-block-title">{t("attendance")}</div>
                                        {loading ? (
                                            <div className="dashboard-empty">{t("loading")}</div>
                                        ) : (
                                            <div className="dashboard-chart-wrap">
                                                <Bar data={attendanceChartData} options={attendanceChartOptions} />
                                            </div>
                                        )}
                                    </div>
                                    <div className="page-block dashboard-chart-block">
                                        <div className="title-with-icon__title dashboard-block-title">{t("topServices")}</div>
                                        {loading ? (
                                            <div className="dashboard-empty">{t("loading")}</div>
                                        ) : topServices.length === 0 ? (
                                            <div className="dashboard-empty">{t("noServicesInDay")}</div>
                                        ) : (
                                            <div
                                                className="dashboard-chart-wrap"
                                                style={{
                                                    minHeight: Math.max(200, topServices.length * 32),
                                                }}
                                            >
                                                <Bar data={topServicesChartData} options={topServicesChartOptions} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="dashboard-side">
                                <div className="page-block dashboard-datetime">
                                    <div className="dashboard-kpi-time">{timeLabel}</div>
                                    <div className="dashboard-kpi-date-row">
                                        <span className="dashboard-kpi-date">{formatDateLong(date, locale)}</span>
                                        <span className="dashboard-kpi-weekday">{formatWeekday(date, locale)}</span>
                                    </div>
                                </div>
                                <aside className="page-block dashboard-sidebar">
                                    <DashboardCalendar date={date} onChange={setDate} />
                                    <div className="dashboard-staff">
                                        <div className="dashboard-staff-title">
                                            {t("staff")}
                                            {!loading ? ` · ${doctors.length}` : ""}
                                        </div>
                                        {loading ? (
                                            <div className="dashboard-empty">{t("loading")}</div>
                                        ) : doctors.length === 0 ? (
                                            <div className="dashboard-empty">
                                                {t("noStaffInYclients")}
                                            </div>
                                        ) : (
                                            <ul className="dashboard-staff-list">
                                                {doctors.map((doc) => (
                                                    <li key={doc.id} className="dashboard-staff-item">
                                                        <span className="dashboard-staff-name">{doc.name}</span>
                                                        <span className="dashboard-staff-count">{doc.count}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </aside>
                            </div>

                            <div className="dashboard-catalog-row">
                                <CatalogCard
                                    to="/staff/"
                                    icon={<IoPeopleOutline />}
                                    label={t("doctors")}
                                    value={catalogCounts.staff == null ? "…" : catalogCounts.staff}
                                />
                                <CatalogCard
                                    to="/services/"
                                    icon={<IoBodyOutline />}
                                    label={t("services")}
                                    value={catalogCounts.services == null ? "…" : catalogCounts.services}
                                />
                                <CatalogCard
                                    to="/contacts"
                                    icon={<IoLocationOutline />}
                                    label={t("clinics")}
                                    value={catalogCounts.clinics == null ? "…" : catalogCounts.clinics}
                                />
                                <CatalogCard
                                    to="/templates/treatment_plans/"
                                    icon={<IoBookmarksOutline />}
                                    label={t("templates")}
                                    value={catalogCounts.templates == null ? "…" : catalogCounts.templates}
                                />
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>
        </Container>
    );
}

export default Dashboard;
